import { ChangeDetectionStrategy, Component, inject, Injector, Input, OnChanges, SimpleChanges } from '@angular/core';
import { combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EmployerPlan } from '../../../../../models/employers/plans/employer-plan';
import { EmployerPlanDesignatedUnit } from '../../../../../models/employers/plans/employer-plan-designated-unit';
import { DesignatedUnitTableType } from '../../../../../models/employers/enum/designated-unit-table-type';
import { DesignatedUnitsTableCardTableService } from './designated-units-table-card-table.service';
import { ModalCreateDesignatedUnit } from '../../../../../modals/modal-create-designated-unit';
import { PortalService } from '../../../../../services/portal/portal.service';
import { Router } from '@angular/router';
import { ButtonToggleOption } from '../../button-toggle/button-toggle.component';
import { Subscribable } from '../../../../../models/base/subscribable';
import { ButtonTypeEnum } from '../../../../../ui/buttons/button/button-type.enum';
import { Routes } from '../../../../../../routes/routes';

@Component({
  selector: 'app-designated-units-table-card',
  templateUrl: './designated-units-table-card.component.html',
  styleUrls: ['./designated-units-table-card.component.scss'],
  providers: [DesignatedUnitsTableCardTableService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DesignatedUnitsTableCardComponent extends Subscribable implements OnChanges {
  @Input() plan!: EmployerPlan;

  private tableService = inject(DesignatedUnitsTableCardTableService);
  private ngbModal = inject(NgbModal);
  private injector = inject(Injector);
  private portalService = inject(PortalService);
  private router = inject(Router);

  public displayedData$ = this.tableService.displayedData$;
  public isLoading$ = this.tableService.isLoading$;
  public totalCount$ = this.tableService.totalItems$;
  public skip$ = this.tableService.skip$;
  public pageSize$ = this.tableService.pageSize$;
  public selectedFilter$ = this.tableService.activeFilter$;
  public readonly$ = this.portalService.isEmployer$;

  public showEmptyState$ = combineLatest([this.totalCount$, this.isLoading$]).pipe(
    map(([count, isLoading]) => !isLoading && count === 0)
  );

  public toggleOptions$ = combineLatest([this.tableService.activeTabCount$, this.tableService.inactiveTabCount$]).pipe(
    map(([activeCount, inactiveCount]): ButtonToggleOption[] => [
      {
        label: activeCount !== null ? $localize`Active (${activeCount})` : $localize`Active`,
        value: DesignatedUnitTableType.Active
      },
      {
        label: inactiveCount !== null ? $localize`Inactive (${inactiveCount})` : $localize`Inactive`,
        value: DesignatedUnitTableType.Inactive
      }
    ])
  );

  public emptyStateConfig$ = this.selectedFilter$.pipe(
    map(filter => ({
      title: filter === DesignatedUnitTableType.Active ? $localize`No Designated Units` : $localize`No Results`,
      description:
        filter === DesignatedUnitTableType.Active
          ? $localize`There are no active designated units`
          : $localize`There are no inactive designated units`,
      buttonText: ''
    }))
  );

  public setFilter(filter: DesignatedUnitTableType): void {
    this.tableService.setActiveFilter(filter);
  }

  public setSkip(skip: number): void {
    this.tableService.setSkip(skip);
  }

  public openNewUnitModal(): void {
    this.tableService.getAllDesignatedUnits().once(units => {
      ModalCreateDesignatedUnit.open(this.ngbModal, this.injector, units, this.plan.id).then(v => {
        if (v) {
          this.tableService.refetch();
        }
      });
    });
  }

  public navigateToUnit(unit: EmployerPlanDesignatedUnit): void {
    this.portalService.isEmployer$.once(isEmployer => {
      const route = isEmployer
        ? Routes.epViewDesignatedUnit(unit.employerPlanId, unit.id)
        : Routes.ipViewDesignatedUnit(this.plan.employerId, unit.employerPlanId, unit.id);
      this.router.navigate([route]).then();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.plan && this.plan) {
      this.tableService.setPlanId(this.plan.id);
    }
  }

  protected readonly ButtonTypeEnum = ButtonTypeEnum;
}
